/**
 * BoardV3 Task Box Content
 * The visual content of a task card (shared between draggable and overlay)
 */

import React, { memo } from 'react';
import type { Task, Employee } from '../../../types';
import type { ScheduledTask } from '../../scheduler';
import { type TaskDetailLevel, BORDER_RADII } from '../../hooks/useBoardTaskDetailLevel';
import { FullContent } from './BoardV3TaskBoxFullContent';

interface BoardV3TaskBoxContentProps {
  task: Task;
  assignees: Employee[];
  detailLevel: TaskDetailLevel;
  scheduled?: ScheduledTask;
  cardWidth?: number;
  isSelected?: boolean;
  isResizing?: boolean;
}

export const BoardV3TaskBoxContent = memo(function BoardV3TaskBoxContent({
  task,
  assignees,
  detailLevel,
  scheduled,
  cardWidth = 200,
  isSelected = false,
  isResizing = false,
}: BoardV3TaskBoxContentProps) {
  const primaryAssignee = assignees[0] || null;
  const hasTeam = assignees.length > 1;

  // Calculated values from schedule
  const effectiveDays = scheduled?.effectiveDays;
  const baseDays = scheduled?.baseDays;
  const isMultitasking = (scheduled?.multitaskingDayCount ?? 0) > 0;
  const teamWeeks = scheduled?.teamWeeks;
  const totalRisk = scheduled?.totalRisk;
  const bestCaseWeeks = scheduled?.bestCaseWeeks;

  const effectiveWeeks = effectiveDays ? Math.ceil(effectiveDays / 5) : null;
  const baseWeeks = baseDays ? Math.ceil(baseDays / 5) : null;
  const bestCaseWeeksRounded = bestCaseWeeks ? Math.ceil(bestCaseWeeks) : null;

  // Best case percentage for overlay
  const bestCasePercent = (effectiveDays && bestCaseWeeks)
    ? Math.min(100, (bestCaseWeeks * 5 / effectiveDays) * 100)
    : 100;

  // Risk level
  const riskLevel = totalRisk !== undefined ? (
    totalRisk > 0.8 ? "high" : totalRisk > 0.5 ? "medium" : "low"
  ) : null;

  // Background color based on assignee
  const backgroundColor = primaryAssignee ? primaryAssignee.color : '#6b7280';

  const borderRadius = BORDER_RADII[detailLevel];

  // Ring styles for resizing/multitasking (selection uses marching ants overlay)
  const ringStyle: React.CSSProperties = isResizing
    ? { outline: '2px solid #3b82f6', outlineOffset: '1px' }
    : isMultitasking
    ? { outline: '2px solid #fb923c', outlineOffset: '1px' }
    : {};

  const showBestCase = detailLevel !== 'minimal' && bestCasePercent < 95 && primaryAssignee;

  return (
    <div
      title={detailLevel === 'minimal' ? task.title : undefined}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius,
        backgroundColor,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        color: '#fff',
        ...ringStyle,
      }}
    >
      {/* Marching ants selection overlay */}
      {isSelected && !isResizing && (
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 20, overflow: 'visible' }}>
          <rect x="0" y="0" width="100%" height="100%" rx={borderRadius} ry={borderRadius} fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth="1.5" />
          <rect x="0" y="0" width="100%" height="100%" rx={borderRadius} ry={borderRadius} fill="none" stroke="white" strokeWidth="1.5" strokeDasharray="6 6" className="marching-ants-stroke" />
        </svg>
      )}

      {/* Best-Case Overlay */}
      {showBestCase && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${bestCasePercent}%`,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: `${borderRadius}px 0 0 ${borderRadius}px`,
            pointerEvents: 'none',
          }}
          title={`Best Case: ${bestCaseWeeksRounded}w`}
        />
      )}

      {/* Best-Case Marker */}
      {showBestCase && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            height: '100%',
            width: 2,
            left: `${bestCasePercent}%`,
            backgroundColor: '#34d399',
            pointerEvents: 'none',
          }}
          title="Best Case Ende"
        />
      )}

      {/* Content */}
      {detailLevel !== 'minimal' && (
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            flexDirection: detailLevel === 'detailliert' ? 'column' : 'row',
            alignItems: detailLevel === 'detailliert' ? 'stretch' : 'center',
            justifyContent: 'space-between',
            height: '100%',
            padding: detailLevel === 'detailliert' ? '2px 6px' : '0 6px',
            gap: detailLevel === 'detailliert' ? 2 : 4,
          }}
        >
          {detailLevel === 'kompakt' && (
            <TitleOnlyContent task={task} primaryAssignee={primaryAssignee} />
          )}
          {detailLevel === 'normal' && (
            <CompactContent
              task={task}
              primaryAssignee={primaryAssignee}
              effectiveWeeks={effectiveWeeks}
            />
          )}
          {detailLevel === 'detailliert' && (
            <FullContent
              task={task}
              assignees={assignees}
              primaryAssignee={primaryAssignee}
              hasTeam={hasTeam}
              effectiveWeeks={effectiveWeeks}
              baseWeeks={baseWeeks}
              bestCaseWeeksRounded={bestCaseWeeksRounded}
              isMultitasking={isMultitasking}
              teamWeeks={teamWeeks}
              riskLevel={riskLevel}
              totalRisk={totalRisk}
              cardWidth={cardWidth}
            />
          )}
        </div>
      )}
    </div>
  );
});

// Title only (kompakt level)
function TitleOnlyContent({
  task,
  primaryAssignee,
}: {
  task: Task;
  primaryAssignee: Employee | null;
}) {
  const tooltip = primaryAssignee
    ? `${task.title} (${primaryAssignee.name})`
    : task.title;

  return (
    <span
      title={tooltip}
      style={{
        fontSize: 10,
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
      }}
    >
      {task.title}
    </span>
  );
}

// Compact view content (single line) - title + estimate badge
function CompactContent({
  task,
  primaryAssignee,
  effectiveWeeks,
}: {
  task: Task;
  primaryAssignee: Employee | null;
  effectiveWeeks: number | null;
}) {
  const tooltip = primaryAssignee
    ? `${task.title} (${primaryAssignee.name})`
    : task.title;

  return (
    <>
      <span
        title={tooltip}
        style={{
          fontSize: 11,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          marginRight: 8,
        }}
      >
        {task.title}
      </span>
      {primaryAssignee && effectiveWeeks !== null && (
        <span
          style={{
            fontSize: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            padding: '1px 4px',
            borderRadius: 3,
            flexShrink: 0,
          }}
        >
          {effectiveWeeks}w
        </span>
      )}
    </>
  );
}

